
<script setup lang="ts">
const props = defineProps({
  title: String,
  imgUrl: String,
  imgAlt: String,
})
const imageLoaded = ref(false)

watch(() => props.imgUrl, () => {
  imageLoaded.value = false
})

const titleParts = computed(() => {
  const rawTitle = props.title?.trim() ?? ''
  const match = rawTitle.match(/^(#\d+)\s+(.*)$/)
  if (!match) {
    return { label: null, heading: rawTitle }
  }

  return {
    label: match[1],
    heading: match[2],
  }
})
</script>

<template>
  <section class="image-text-section">
    <div class="image-text-section__visual" :aria-busy="!imageLoaded">
      <Skeleton v-if="!imageLoaded" class="image-text-section__skeleton" aria-hidden="true" />
      <NuxtImg
        loading="lazy"
        quality="90"
        format="webp"
        :src="imgUrl!"
        :alt="imgAlt || `${titleParts.heading} - MODY Veranstaltungstechnik`"
        class="image-text-section__image"
        :class="{ 'image-text-section__image--loaded': imageLoaded }"
        @load="imageLoaded = true"
        @error="imageLoaded = true"
      />
      <div class="image-text-section__overlay"></div>
      <div class="image-text-section__heading-wrap">
        <p v-if="titleParts.label" class="image-text-section__label">{{ titleParts.label }}</p>
        <h2 class="image-text-section__heading">{{ titleParts.heading }}</h2>
      </div>
    </div>

    <div class="image-text-section__body">
      <div class="image-text-section__copy">
        <slot></slot>
      </div>
    </div>
  </section>
</template>

<style scoped lang="scss">
.image-text-section{
  background: #0f1012;
}

.image-text-section__visual{
  position: relative;
  overflow: hidden;
  min-height: 220px;
  background: $public-surface-raised;
}

.image-text-section__skeleton{
  position: absolute;
  inset: 0;
  width: 100% !important;
  height: 100% !important;
  border-radius: 0 !important;
}

.image-text-section__image{
  width: 100%;
  height: 220px;
  object-fit: cover;
  opacity: 0;
  transition: opacity 220ms $public-ease;

  @include desktop(){
    height: 320px;
  }
}

.image-text-section__image--loaded{
  opacity: 0.82;
}

.image-text-section__overlay{
  position: absolute;
  inset: 0;
  background: linear-gradient(180deg, rgba(11,11,12,0.18) 0%, rgba(11,11,12,0.36) 56%, rgba(11,11,12,0.72) 100%);
}

.image-text-section__heading-wrap{
  position: absolute;
  left: 1.5rem;
  right: 1.5rem;
  bottom: 1.5rem;
  z-index: 1;

  @include desktop(){
    left: 6rem;
    right: 6rem;
    bottom: 3.5rem;
  }
}

.image-text-section__label{
  margin: 0 0 0.75rem;
  color: rgba(243,242,238,0.72);
  font-size: 12px;
  font-weight: 600;
  letter-spacing: 0.18em;
  line-height: 16px;
  text-transform: uppercase;
}

.image-text-section__heading{
  margin: 0;
  color: rgb(245 245 244);
  font-size: 44px;
  font-weight: 650;
  letter-spacing: -0.04em;
  line-height: 0.98;
  max-width: 10ch;

  @include desktop(){
    font-size: 78px;
    max-width: 11ch;
  }
}

.image-text-section__body{
  padding: 2rem 1.5rem 2.5rem;

  @include desktop(){
    padding: 2.75rem 6rem 3.25rem;
  }
}

.image-text-section__copy{
  width: 100%;
  color: rgb(214 211 209);
  font-size: 18px;
  font-weight: 300;
  letter-spacing: 0.01em;
  line-height: 1.72;

  :deep(a){
    text-decoration: none;
  }

  :deep(.p-button){
    border-radius: $public-radius-control;
    color: #111214;
    min-height: 44px;
    padding-inline: 1rem;
  }

  @include desktop(){
    max-width: 760px;
    margin: 0 auto;
    font-size: 20px;
    line-height: 1.62;
  }
}
</style>
